<template>
  <div class="animated fadeIn">
    <export-modal
      v-model="csvModal.show"
      file-type="csv"
      :file-link="csvModal.link"
      :alert-text="csvModal.alertText"
    />

    <h1>Global Trade Flow Monitoring</h1>
    <b-row>
      <b-col cols="12">
        <filters
          @search="applyFilterHandler"
          @search-immediately="applyFilterImmidately"
          :apiRequestIsPerforming="!!debouncedActiveRequestsCount"
          :apiRequestData="apiRequestData"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="3" md="6" class="d-flex">
        <directions-stat
          class="tradeflow-card"
          title="Top Origins"
          :countries="originsStat.countries"
          :ports="originsStat.ports"
        />
      </b-col>
      <b-col lg="3" md="6" class="d-flex">
        <directions-stat
          class="tradeflow-card"
          title="Top Destinations"
          :countries="destinationsStat.countries"
          :ports="destinationsStat.ports"
        />
      </b-col>
      <b-col lg="6">
        <b-card no-body class="tradeflow-card">
          <template #header>Port Map</template>
          <b-card-body body-class="p-0">
            <base-map
              :map-zoom="1"
              :origin-bubbles="originPortsFormatted"
              :destination-bubbles="destinationPortsFormatted"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12" class="d-flex">
        <periodic-stats
          class="tradeflow-card"
          :title="timeseriesTitle"
          :direction="directionText"
          :yAxisLabel="`${directionText} ${metricType}`"
          chartId="timeseries"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :metric-enabled="true"
          :metric-choices="metricChoices"
          :data-labels="true"
          :stats="periodicStats"
          @search="applyFilterHandler"
          :apiRequestIsPerforming="!!debouncedActiveRequestsCount"
          :export-types="exportTypes"
          @export="format => handleExportTimeseries(format)"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col lg="12" class="d-flex">
        <periodic-stats
          class="tradeflow-card"
          :title="netExportsTitle"
          :direction="directionText"
          :yAxisLabel="'Net ' + directionText + ' Volume'"
          chartId="netexports"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :data-labels="true"
          :stats="netExportsStats"
          @search="applyFilterHandler"
          :apiRequestIsPerforming="!!debouncedActiveRequestsCount"
          :export-types="exportTypes"
          @export="format => handleExportTimeseries(format)"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col lg="6" md="6" class="d-flex">
        <segments-chart
          class="tradeflow-card"
          title="Vessel Classes"
          :items="vesselsStats"
        />
      </b-col>
      <b-col lg="6" md="6" class="d-flex">
        <movements-stat class="tradeflow-card" :stats="movementsStat" />
      </b-col>
    </b-row>
    <b-row>
      <b-col lg="12" class="d-flex">
        <voyages
          :items="voyages.items"
          :currentPage="voyages.currentPage"
          @page-changed="voyagesPageChanged"
          :export-types="exportTypes"
          @export="format => handleExportVoyages(format)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash-es/debounce";
import getNested from "lodash-es/get";
import pickBy from "lodash-es/pickBy";

import { formatPorts } from "@/shared/api_formatters/ports";
import Voyages from "../components/tradeflows/Voyages";
import DirectionsStat from "../components/tradeflows/DirectionsStat";
import MovementsStat from "../components/tradeflows/MovementsStat";
import Filters from "../components/tradeflows/Filters";
import PeriodicStats from "../components/tradeflows/PeriodicStats";
import SegmentsChart from "../components/widgets/SegmentsChart";
import ExportModal from "../components/widgets/ExportModal";
import BaseMap from "@/base_components/BaseMap";
import { formatVolume } from "@/shared/number-utils";

export default {
  name: "tradeflows",

  components: {
    BaseMap,
    ExportModal,
    Filters,
    Voyages,
    DirectionsStat,
    PeriodicStats,
    MovementsStat,
    SegmentsChart
  },

  data() {
    const defaultParameterNames = [
      // note: api names used here (not field names)
      "subSegment",
      "commodity",
      "fromRegion",
      "toRegion",
      "fromCountryCode",
      "toCountryCode",
      "range",
      "fromDate",
      "toDate",
      // 'flowDate',
      "excludeIntraCountry",
      "excludeUnknownDestinations",
      "parceling"
    ];

    return {
      debouncedActiveRequestsCount: 1,
      apiRequestData: {
        voyages: {},
        voyagesPeriodicStat: {}
      }, // getVoyages, getVoyagesPeriodicStat
      exportTypes: ["csv"],

      initialLoadRequestsCount: 1, // requests amount when filter is changed
      preloadRequestsCount: 1, // requests amount which will be performed on load
      preloadPagesRange: 5, // if (totalPages - currentPage) < preloadPagesRange
      // load another "preloadRequestsCount" requests
      filterParameters: {
        range: "monthly",
        group: null,
        metric: "volume",
        commodity: [],
        fromCountryCode: [],
        toCountryCode: [],
        segment: [],
        fromDate: "",
        toDate: "",
        flowDate: ""
      },
      voyages: {
        items: [],
        apiItemsPerPage: 50, // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null, // or max_page, it doesn't matter since pages are starts from "1"
        currentPage: null
      },
      originsStat: {
        countries: [],
        ports: []
      },
      destinationsStat: {
        countries: [],
        ports: []
      },
      periodicStats: [],
      netExportsStats: [],
      vesselsStats: [],
      movementsStat: {},

      csvModal: {
        show: false,
        link: null,
        alertText: null
      },

      requiredParameters: {
        // for API which requires only a subset of all filter parameters
        // note: api names used here (not field names)
        topOrigins: [...defaultParameterNames, "flowDate"],
        topDestinations: [...defaultParameterNames, "flowDate"],
        periodicStats: [...defaultParameterNames, "flowDate", "group", "metric"],
        netExportsStats: [...defaultParameterNames, "flowDate", "group"],
        vesselsStats: [...defaultParameterNames, "flowDate"],
        movementsStat: [...defaultParameterNames, "flowDate"],
        voyages: [
          "subSegment",
          "commodity",
          "fromRegion",
          "fromCountryCode",
          "toRegion",
          "toCountryCode",
          "fromDate",
          "toDate",
          "excludeIntraCountry",
          "excludeUnknownDestinations",
          "parceling",
          "flowDate"
        ],
      },

      metricChoices: [
        { text: "Volume", value: "volume" },
        { text: "Voyage count", value: "count" },
        { text: "Ton miles", value: "ton_mile_calculated" },
        //{ text: "Ton miles", value: "ton_mile_actual" },
        { text: "Average haul (nautical miles)", value: "avg_haul" },
        { text: "Days (steaming at sea)", value: "sum_days_steaming" },
        { text: "Days (total duration)", value: "sum_days_total_duration" },
        { text: "Volume * duration", value: "volume_times_duration" },
      ],
    };
  },

  methods: {
    ...mapActions("api/tradeflows", [
      "getVoyages",
      "getCountriesExportStat",
      "getPortsExportStat",
      "getCountriesImportStat",
      "getPortsImportStat",
      "getVoyagesPeriodicStat",
      "getNetExportsStat",
      "getVesselsStat",
      "getMovementsStat"
    ]),

    setFakeRequestsCount: function(value) {
      this.debouncedActiveRequestsCount = value;
    },

    resetDashboardData: function() {
      // when something wrong is selected in controls we need to empty all content

      this.apiRequestData = {
        voyages: {},
        voyagesPeriodicStat: {}
      };

      this.voyages = {
        apiItemsPerPage: 50,
        apiTotalPages: null,
        currentPage: null,
        items: []
      };

      this.periodicStats = [];
      this.netExportsStats = [];
      this.vesselsStats = [];
      this.movementsStat = {};

      this.originsStat = { countries: [], ports: [] };
      this.destinationsStat = { countries: [], ports: [] };
    },

    filterParametersByNames(filterParameters, requiredFields) {
      // removes redundant parameters
      // (keeps only requiredFields from filterParameters)
      return pickBy(filterParameters, (value, name) => {
        return requiredFields.includes(name);
      });
    },

    applyFilterHandler: debounce(async function(filterParameters) {
      this.setFakeRequestsCount(1);
      return await this.applyFilter(filterParameters);
    }, 1500),

    async applyFilterImmidately(filterParameters) {
      this.setFakeRequestsCount(1);
      return await this.applyFilter(filterParameters);
    },

    async applyFilter(filterParameters) {
      window.analytics.track("TradeFlowSearch", this.filterParameters);

      this.filterParameters = { ...this.filterParameters, ...filterParameters };

      if (!this.filterParameters.commodity) {
        this.setFakeRequestsCount(0);
        this.resetDashboardData();
      } else if (filterParameters.range) {
        // if range selector is updated: update only periodic graph
        if (filterParameters.chart === "timeseries") {
          await this.loadPeriodicStats(this.filterParameters);
        } else {
          await this.loadNetExportStats(this.filterParameters);
        }
      } else if (filterParameters.group) {
        // if group selector is updated: update only periodic graph
        if (filterParameters.chart === "timeseries") {
          await this.loadPeriodicStats(this.filterParameters);
        } else {
          await this.loadNetExportStats(this.filterParameters);
        }
      } else {
        // if top filters updated: update all graphs + periodic graph
        await Promise.all([
          this.loadTopOrigins(this.filterParameters),
          this.loadTopDestinations(this.filterParameters),
          this.loadPeriodicStats(this.filterParameters),
          this.loadNetExportStats(this.filterParameters),
          this.loadVesselsStat(this.filterParameters),
          this.loadMovementsStat(this.filterParameters),
          this.loadVoyages(this.filterParameters)
        ]);
      }
    },

    async handleExportTimeseries(format) {
      window.analytics.track(
        "TradeflowsTimeseriesExport",
        this.filterParameters
      );
      let response = await this.getVoyagesPeriodicStat({
        ...this.filterParameters,
        format
      });

      this.csvModal.link = response.data.csv;
      this.csvModal.alertText = null;
      this.csvModal.show = true;
      // const timestamp = moment().format('YYYY-MM-DD-HH-mm');
      // downloadAsFile(response, `tradeflow_timeseries_export_${timestamp}.${format}`);
    },

    async handleExportTimeseriesNetExport(format) {
      window.analytics.track(
        "TradeflowsTimeseriesNetExport",
        this.filterParameters
      );
      let response = await this.getNetExportsStat({
        ...this.filterParameters,
        format
      });

      this.csvModal.link = response.data.csv;
      this.csvModal.alertText = null;
      this.csvModal.show = true;
      // const timestamp = moment().format('YYYY-MM-DD-HH-mm');
      // downloadAsFile(response, `tradeflow_timeseries_export_${timestamp}.${format}`);
    },

    async handleExportVoyages(format) {
      window.analytics.track("TradeflowsVoyagesExport", this.filterParameters);
      const limit = 20000;
      let response = await this.getVoyages({
        ...this.filterParameters,
        format,
        limit: limit
      });
      this.csvModal.link = response.data.csv;
      this.csvModal.alertText = `CSV download for voyages is currently limited to ${limit} rows per download`;
      this.csvModal.show = true;
      // const timestamp = moment().format('YYYY-MM-DD-HH-mm');
      // downloadAsFile(response, `tradeflow_timeseries_export_${timestamp}.${format}`);
    },

    async loadVoyages(filterParameters) {
      // it's possible to have a lot of pages here
      // they are loaded async to prevent interface lags

      this.voyages.items = [];
      this.voyages.currentPage = 1;

      let parameters = this.filterParametersByNames(
        filterParameters,
        this.requiredParameters.voyages
      );
      parameters.page = 1;
      await this.fetchVoyages(parameters, this.initialLoadRequestsCount);
    },

    async voyagesPageChanged(currentPage, totalPages) {
      // voyages on demand loading, overview:
      // 1. detect when a current page is less than "preloadPagesRange" value
      // 2. add items which are received by "preloadRequestsCount" requests

      if (this.getActiveRequestsCount) {
        return; // prevent multiple requests
      }

      // calculate an API page number to start
      let apiPagesLoaded =
        this.voyages.items.length / this.voyages.apiItemsPerPage;

      if (
        totalPages - currentPage < this.preloadPagesRange &&
        Number.isInteger(apiPagesLoaded)
      ) {
        let parameters = { ...this.filterParameters, page: apiPagesLoaded + 1 };

        await this.fetchVoyages(parameters, this.preloadRequestsCount);
        // b-pagination resets a current page when data is changed we have to restore it this way
        // https://github.com/bootstrap-vue/bootstrap-vue/issues/3372
        this.voyages.currentPage = currentPage;
      }
    },

    async fetchVoyages(filterParameters, requestsCount) {
      let countdown = requestsCount; // no more than countdown requests
      let parameters = { ...filterParameters };
      let response = null;

      do {
        response = (await this.getVoyages(parameters)) || {};

        if (response.data && response.data.data) {
          this.voyages.items.push.apply(this.voyages.items, response.data.data);
          this.voyages.apiTotalPages = response.data.maxPage;
          parameters.page += 1;
        } else {
          break;
        }
        countdown--;
      } while (parameters.page <= response.data.maxPage && countdown);

      this.apiRequestData.voyages = response.requestData;
    },

    async loadTopOrigins(filterParameters) {
      let parameters = this.filterParametersByNames(
        filterParameters,
        this.requiredParameters.topOrigins
      );
      this.originsStat.countries = await this.getCountriesExportStat(
        parameters
      );
      this.originsStat.ports = await this.getPortsExportStat(parameters);
    },

    async loadTopDestinations(filterParameters) {
      let parameters = this.filterParametersByNames(
        filterParameters,
        this.requiredParameters.topDestinations
      );
      this.destinationsStat.countries = await this.getCountriesImportStat(
        parameters
      );
      this.destinationsStat.ports = await this.getPortsImportStat(parameters);
    },

    async loadPeriodicStats(filterParameters) {
      // note: seasonal switch will work only if sufficient dateRange is selected! (with previous years)
      let parameters = this.filterParametersByNames(
        filterParameters,
        this.requiredParameters.periodicStats
      );
      let response = (await this.getVoyagesPeriodicStat(parameters)) || {};
      this.periodicStats = response.data || [];
      this.apiRequestData.voyagesPeriodicStat = response.requestData;
    },

    async loadNetExportStats(filterParameters) {
      // note: seasonal switch will work only if sufficient dateRange is selected! (with previous years)
      let parameters = this.filterParametersByNames(
        filterParameters,
        this.requiredParameters.netExportsStats
      );
      let response = (await this.getNetExportsStat(parameters)) || {};
      this.netExportsStats = response.data || [];
    },

    async loadVesselsStat(filterParameters) {
      let parameters = this.filterParametersByNames(
        filterParameters,
        this.requiredParameters.vesselsStats
      );
      this.vesselsStats = await this.getVesselsStat(parameters);
    },

    async loadMovementsStat(filterParameters) {
      let parameters = this.filterParametersByNames(
        filterParameters,
        this.requiredParameters.movementsStat
      );
      this.movementsStat = (await this.getMovementsStat(parameters)) || {};
    }
  },

  computed: {
    ...mapGetters("api", ["getActiveRequestsCount"]),

    originPortsFormatted() {
      return formatPorts(this.originsStat.ports)
        .filter(item => !!item.coords)
        .map(item => ({
          ...item,
          popupData: {
            Name: item.name,
            Volume: formatVolume(item.volume),
            "Country Code": item.countryCode
          }
        }));
    },

    destinationPortsFormatted() {
      return formatPorts(this.destinationsStat.ports).filter(
        item => !!item.coords
      );
    },

    netExportsTitle() {
      if (this.filterParameters.flowDate === "import") {
        return "Net Exports (volume)";
      } else {
        return "Net Exports (volume)";
      }
    },

    metricType() {
      const metricItem = this.metricChoices.find(item => item.value === this.filterParameters.metric);
      return getNested(metricItem, 'type', 'volume');
    },

    timeseriesTitle() {
      if (this.filterParameters.flowDate === "import") {
        return `Import ${this.metricType} by time period`;
      } else {
        return `Export ${this.metricType} by time period`;
      }
    },
    directionText() {
      if (this.filterParameters.flowDate === "import") {
        return "Import";
      } else {
        return "Export";
      }
    }
  },

  watch: {
    getActiveRequestsCount: debounce(function(newValue) {
      this.debouncedActiveRequestsCount = newValue;
    }, 200)
  }
};
</script>

<style scoped>
.tradeflow-card >>> .card-body {
  /* deep-scoped style, affects child components */
  height: 370px;
}
</style>
